import React, { useContext, useState } from "react"
import WizardWrapper from "components/views/atoms/forms/WizardWrapper"
import RequestCardHeader from "components/views/requestcard/RequestCardHeader"
import L10n from "lib/carrot-l10n"
import { Spacer, HR } from "components/views/atoms/Atoms"
import { Title } from "../../../services/page-title/Title"
import RequestCardWizardHeader from "components/views/requestcard/RequestCardWizardHeader"
import ErrorMessageMarkdown from "../login/ErrorMessageMarkdown"
import { Body } from "../atoms/Bodies"
import { MasterLayout } from "../atoms/layouts/MasterLayout"
import { BodyWithRightSidebarLayout } from "../atoms/layouts/BodyWithRightSidebarLayout"
import { getIsUsa } from "reducers/userInfo"
import { useSelector } from "react-redux"
import { CompleteFormSelect } from "components/views/atoms/forms/FormSelect"
import { CompletePrefixInput } from "components/views/atoms/forms/FormPrefixInput"
import { PhoneCodeSelectOptions } from "content/PhoneCodeSelectOptions"
import { PhoneCodes } from "content/PhoneCodes"
import { getCountryCode } from "reducers/companyInfo"
import { HttpErrors } from "utils/HttpErrors"
import CarrotClient from "carrot-api"
import Settings from "utils/CarrotConfig"
import { useForm } from "react-hook-form"
import { RequestCardContext } from "components/containers/requestcard/RequestCardContainer"
import { useHistory } from "react-router"
import { CountryCode } from "content/CountryMap"
import EmployeeOnlySpaceContainer from "../../employee-only-containers/EmployeeOnlySpaceContainer"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()
const client = new CarrotClient(Settings.CARROT_BACKEND_URL)

type ConfirmPhoneNumberFormData = {
  countryCode: CountryCode
  phoneNumber: string
}

const ConfirmPhoneNumber = (props) => {
  const { completeConfirmPhoneNumber } = useContext(RequestCardContext)
  const isUsa = useSelector(getIsUsa)
  const history = useHistory()
  const countryCode = useSelector(getCountryCode)
  const { handleSubmit, register, formState, setError } = useForm<ConfirmPhoneNumberFormData>({
    defaultValues: { countryCode }
  })

  const [state, setState] = useState({
    isValidPhoneNumber: true,
    isInError: false,
    sending: false,
    selectedCountryCode: countryCode
  })

  const handleCountryCodeChange = (event) => {
    setState({ ...state, selectedCountryCode: event.target.value })
  }

  const onSubmit = handleSubmit(async (formValues) => {
    const { phoneNumber, countryCode: countryCodeFormValue } = formValues
    const sanitizedPhoneNumber = phoneNumber.startsWith("+") ? phoneNumber.substring(1) : phoneNumber

    let countryCode = countryCodeFormValue
    countryCode = countryCode ?? "US"

    const phoneCountryCode = PhoneCodes[countryCode]
    const phoneNumberWithCountryCode = `${phoneCountryCode}${sanitizedPhoneNumber}`

    try {
      const response = await client.startPhoneVerification(phoneNumberWithCountryCode, countryCode)
      const standardizedPhoneNumber = await response.json()

      setState({
        ...state,
        isInError: false,
        isValidPhoneNumber: true,
        sending: false
      })

      completeConfirmPhoneNumber(standardizedPhoneNumber, countryCode)
      history.push(props.requestCardPath + "/phone-verification")
    } catch (e) {
      if (e.name === HttpErrors.NOT_FOUND) {
        setState({ ...state, isValidPhoneNumber: false, sending: false })
      } else {
        setState({
          ...state,
          isInError: true,
          isValidPhoneNumber: true,
          sending: false
        })
      }
    }
  })

  return (
    <MasterLayout
      header={<RequestCardHeader backRoute={isUsa ? props.requestCardPath + "/mailing-address" : "/transactions"} />}
    >
      <EmployeeOnlySpaceContainer>
        <Title title={isUsa ? l`carrotCard.getYourCarrotCard` : l`carrotCard.getYourVirtualCarrotCard`} />
        <BodyWithRightSidebarLayout>
          <RequestCardWizardHeader step={isUsa ? 2 : 1}>
            <span id="request-card-confirm-phone-number-header">{l`carrotCard.request.confirmPhoneNumber`}</span>
          </RequestCardWizardHeader>
          <WizardWrapper
            back={isUsa}
            backText={l`back`}
            backRoute={props.requestCardPath + "/mailing-address"}
            done
            doneText={l`Send verification code`}
            nextId="request-card-confirm-phone-number-next-button"
            onDone={onSubmit}
            nextDisabled={!formState.isValid && !formState.isDirty}
            sending={formState.isSubmitting}
          >
            {state.isInError ? (
              <ErrorMessageMarkdown align="tl" source={l`carrotCard.request.error`} />
            ) : (
              <div>
                <Body color="black-80">
                  {isUsa ? l`carrotCard.request.receiveTextAlert` : l`carrotCard.request.receiveTextAlertInternational`}
                </Body>
                {isUsa && (
                  <>
                    <Spacer height={1.5} />
                    <Body color="black-80">{l`carrotCard.request.verifyPhoneNumber`}</Body>
                  </>
                )}
              </div>
            )}
            <Spacer height={1.5} />
            <CompleteFormSelect
              {...register("countryCode")}
              label={"country code"}
              options={PhoneCodeSelectOptions}
              errors={{}}
              readOnly={false}
              valid={true}
              onChange={handleCountryCodeChange}
            />
            <CompletePrefixInput
              {...register("phoneNumber", {
                validate: () => {
                  setError("phoneNumber", { type: "string", message: "Please enter a valid phone number" })
                  return state.isValidPhoneNumber
                }
              })}
              label={l`phone number`}
              prefixText={`+${PhoneCodes[state.selectedCountryCode]}`}
              showValidation
              errors={formState.errors}
              readOnly={false}
            />
            <Spacer height={1} />
          </WizardWrapper>
          <Spacer height={2} />
          <HR color="black-10" />
        </BodyWithRightSidebarLayout>
      </EmployeeOnlySpaceContainer>
    </MasterLayout>
  )
}

export { ConfirmPhoneNumber }
